import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';

import { apiClient, type MeaningResult } from '@/services';
import { cn } from '@/services/utils';

const MAX_CARD_SUGGESTIONS = 5;
const SUGGESTION_DEBOUNCE_MS = 200;

type CardSuggestion = {
  id: string;
  phrase: string;
  definition: string;
};

const toCardSuggestion = (meaning: MeaningResult): CardSuggestion | null => {
  const nested = meaning.meaning;
  const phrase = [
    (nested as unknown as { displayedText?: string } | undefined)?.displayedText,
    nested?.dictionaryForm,
    nested?.textInput,
  ].find((value) => value?.trim())?.trim();
  if (!meaning.id || !phrase) return null;
  return {
    id: meaning.id,
    phrase,
    definition: nested?.definition?.trim() || nested?.translation?.trim() || '',
  };
};

function HighlightedPhrase({ phrase, query }: { phrase: string; query: string }) {
  const start = phrase.toLowerCase().indexOf(query.toLowerCase());
  if (start < 0) return <>{phrase}</>;
  const end = start + query.length;
  return (
    <>
      {phrase.slice(0, start)}
      <strong className="font-semibold text-[#2f5c4e]">{phrase.slice(start, end)}</strong>
      {phrase.slice(end)}
    </>
  );
}

export function HeaderSearch({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const listboxId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(true);
  const [suggestions, setSuggestions] = useState<CardSuggestion[]>([]);
  const [suggestionsFor, setSuggestionsFor] = useState('');
  const [suggestionsFailed, setSuggestionsFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const trimmedQuery = query.trim();
  const visibleSuggestions = suggestionsFor === trimmedQuery ? suggestions : [];
  const searchOptionIndex = visibleSuggestions.length;
  const showDropdown = open && trimmedQuery.length > 0;

  useEffect(() => {
    if (!trimmedQuery) return;
    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      apiClient.getPagedMeanings(null, MAX_CARD_SUGGESTIONS, null, trimmedQuery)
        .then((page) => {
          if (cancelled) return;
          setSuggestions((page.items ?? []).map(toCardSuggestion).filter((card): card is CardSuggestion => card !== null));
          setSuggestionsFailed(false);
          setSuggestionsFor(trimmedQuery);
          setActiveIndex(-1);
        })
        .catch(() => {
          if (cancelled) return;
          setSuggestions([]);
          setSuggestionsFailed(true);
          setSuggestionsFor(trimmedQuery);
          setActiveIndex(-1);
        });
    }, SUGGESTION_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [trimmedQuery]);

  const reset = () => {
    setQuery('');
    setActiveIndex(-1);
    onClose();
  };

  const openCard = (card: CardSuggestion) => {
    reset();
    navigate(`/meaning/card/${card.id}`);
  };

  const runTextSearch = () => {
    if (!trimmedQuery) return;
    const target = `/videos?q=${encodeURIComponent(trimmedQuery)}`;
    reset();
    navigate(target);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (showDropdown) {
        setOpen(false);
      } else {
        reset();
      }
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const card = visibleSuggestions[activeIndex];
      if (showDropdown && card) {
        openCard(card);
      } else {
        runTextSearch();
      }
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!trimmedQuery) return;
      event.preventDefault();
      setOpen(true);
      const optionCount = searchOptionIndex + 1;
      setActiveIndex((current) =>
        event.key === 'ArrowDown'
          ? (current + 1) % optionCount
          : (current <= 0 ? optionCount : current) - 1
      );
    }
  };

  const optionId = (index: number) => `${listboxId}-option-${index}`;

  return (
    <div className="relative w-full">
      <Search className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-[#57534a]" aria-hidden="true" />
      <input
        autoFocus
        type="text"
        role="combobox"
        aria-label="Search words or tags"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={showDropdown && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          if (!trimmedQuery) onClose();
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        maxLength={200}
        placeholder="Search words or tags"
        className="h-11 w-full rounded-full border-[1.5px] border-[#2f5c4e] bg-white pl-[46px] pr-10 text-base text-[#1e1c18] outline-none placeholder:text-[#6b6659]"
      />
      <button
        type="button"
        aria-label="Close search"
        onMouseDown={(event) => event.preventDefault()}
        onClick={reset}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b6659] hover:text-[#1e1c18]"
      >
        <X className="size-4" />
      </button>

      {showDropdown && (
        <div
          onMouseDown={(event) => event.preventDefault()}
          className="absolute inset-x-0 top-[52px] z-30 flex flex-col gap-0.5 rounded-2xl border border-[#e6e2da] bg-white p-2 shadow-[0_12px_32px_rgba(30,28,24,0.14)]"
        >
          {visibleSuggestions.length === 0 && suggestionsFor === trimmedQuery && !suggestionsFailed && (
            <div role="status" className="px-3 pb-1.5 pt-2.5 text-sm text-[#6b6659]">No card for “{trimmedQuery}” yet</div>
          )}

          <div id={listboxId} role="listbox" aria-label="Search suggestions" className="flex flex-col gap-0.5">
            {visibleSuggestions.length > 0 && (
              <div role="group" aria-labelledby={`${listboxId}-cards`} className="flex flex-col gap-0.5">
                <div id={`${listboxId}-cards`} className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-[0.06em] text-[#6b6659]">
                  Your cards
                </div>
                {visibleSuggestions.map((card, index) => (
                  <div
                    key={card.id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={activeIndex === index}
                    onClick={() => openCard(card)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex min-h-14 cursor-pointer flex-col justify-center gap-0.5 rounded-[10px] px-3 py-2',
                      activeIndex === index && 'bg-[#f6f4ef]'
                    )}
                  >
                    <span className="truncate whitespace-pre font-[family-name:var(--font-display)] text-[17px] font-medium text-[#1e1c18]">
                      <HighlightedPhrase phrase={card.phrase} query={trimmedQuery} />
                    </span>
                    {card.definition && (
                      <span className="truncate text-[13px] text-[#6b6659]">{card.definition}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {visibleSuggestions.length > 0 && <div className="mx-1 my-1.5 h-px bg-[#e6e2da]" aria-hidden="true" />}

            <div
              id={optionId(searchOptionIndex)}
              role="option"
              aria-selected={activeIndex === searchOptionIndex}
              onClick={runTextSearch}
              onMouseEnter={() => setActiveIndex(searchOptionIndex)}
              className={cn(
                'flex min-h-14 cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2',
                activeIndex === searchOptionIndex && 'bg-[#f6f4ef]'
              )}
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#e3ece7]" aria-hidden="true">
                <Search className="size-[18px] text-[#2f5c4e]" strokeWidth={2} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[15px] font-medium text-[#1e1c18]">
                  Search for “<strong className="font-semibold">{trimmedQuery}</strong>”
                </span>
                <span className="text-[13px] text-[#6b6659]">Search by text, no card needed</span>
              </span>
              <span className="shrink-0 rounded-md border border-[#d9d5c9] px-2 py-0.5 text-[13px] text-[#6b6659]" aria-hidden="true">
                Enter ↵
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
